<template>
  <BittsModal
    class="c-webhook-config-modal"
    :aria-label="isEdit ? 'Edit Webhook' : 'Create Webhook'"
    variant="data"
    :width="600"
    :primary-button-text="isEdit ? 'Save Changes' : 'Finish'"
    secondary-button-text="Cancel"
    :disabled="isDisabled"
    :action-loading="isCreating"
    @action="handleCreate"
    @closed="handleClose"
  >
    <template #header>
      <div>
        <h2 class="text-neutral-text-strong font-bold text-xl pb-8">
          {{ isEdit ? 'Edit Webhook' : 'Create Webhook' }}
        </h2>
        <p class="text-neutral-text text-m">
          Send real-time Crossbeam updates to your external apps or systems.
          Choose the events you want to subscribe to and add your endpoint URL.
          <BittsLink
            text="More about our webhooks"
            url="https://help.crossbeam.com/en/articles/12732223-getting-started-with-signals-how-to-access-real-time-partner-data-via-api-and-webhook"
            :open-in-new-tab="true"
          />
        </p>
      </div>
    </template>
    <div class="p-24">
      <div class="space-y-16">
        <div>
          <label
            class="block text-neutral-text-strong font-medium text-sm mb-8"
          >
            Select events
          </label>
          <BittsMultiselect
            aria-label="Event types"
            v-model="selectedEventTypes"
            :options="eventTypeOptions"
            mode="multiple"
            placeholder="Select event types"
            caption="Choose which Crossbeam events will trigger this webhook"
            :disabled="isCreating"
          />
        </div>

        <div>
          <label
            class="block text-neutral-text-strong font-medium text-sm mb-8"
          >
            Webhook name
          </label>
          <BittsInput
            v-model="webhookName"
            placeholder="Enter a webhook name"
            caption="Give this webhook a name to help you identify it later"
            name="webhook-name"
            type="text"
            :disabled="isCreating"
          />
        </div>

        <div>
          <label
            class="block text-neutral-text-strong font-medium text-sm mb-8"
          >
            Destination URL
          </label>
          <BittsInput
            v-model="webhookUrl"
            placeholder="https://your-app.com/webhook"
            name="webhook-url"
            type="text"
            :status="webhookUrlStatus"
            :danger-text="webhookUrlError || ''"
            :disabled="isCreating"
          />
        </div>
        <div v-if="!isEdit" class="text-neutral-500 text-sm">
          By clicking “Finish”, you agree to the
          <BittsLink
            text="Crossbeam Developer Terms."
            url="https://www.crossbeam.com/legal/developers/"
            :open-in-new-tab="true"
          />
        </div>
      </div>
    </div>
  </BittsModal>
</template>

<script setup lang="ts">
import {
  BittsInput,
  BittsLink,
  BittsModal,
  BittsMultiselect,
} from '@crossbeam/bitts';
import { AddSelectProperties } from '@crossbeam/types';

import { computed, ref, watch } from 'vue';

import { WEBHOOK_EVENT_TYPES } from '@/constants/webhooks';
import { useIntegrationProfileStore, useWebhooksStore } from '@/stores';
import { Webhook, WebhookEventType } from '@/types/webhooks';

const {
  isEdit = false,
  webhookData = undefined,
  initialEventTypes = [],
} = defineProps<{
  isEdit?: boolean;
  webhookData?: Webhook;
  initialEventTypes?: string[];
}>();

const emit = defineEmits(['action', 'closed', 'webhook-created']);

// Event type options (NNA is intentionally excluded from the visible dropdown)
const eventTypeOptions = computed<AddSelectProperties[]>(() => [
  {
    value: WEBHOOK_EVENT_TYPES.PARTNER_DEAL_OPENED,
    label: 'Partner Deal Opened',
  },
  {
    value: WEBHOOK_EVENT_TYPES.PARTNER_DEAL_CLOSED_WON,
    label: 'Partner Deal Closed-Won',
  },
  {
    value: WEBHOOK_EVENT_TYPES.OWN_DEAL_OPENED,
    label: 'Own Deal Opened',
  },
  {
    value: WEBHOOK_EVENT_TYPES.OWN_DEAL_CLOSED_WON,
    label: 'Own Deal Closed-Won',
  },
]);

const visibleEventTypeValues = computed(() =>
  eventTypeOptions.value.map((opt) => opt.value),
);

// Form data
const selectedEventTypes = ref<string[]>(
  initialEventTypes.filter((type) =>
    visibleEventTypeValues.value.includes(type),
  ),
);
const hiddenEventTypes = ref<string[]>(
  initialEventTypes.filter(
    (type) => !visibleEventTypeValues.value.includes(type),
  ),
);
const webhookUrl = ref('');
const webhookName = ref('');
const isCreating = ref(false);

// URL validation: https only, public DNS hostname (no IP literals, no localhost
// or internal hosts). The backend enforces the same rules (DUK-250); this is
// fast client-side feedback and the server stays authoritative.
const webhookUrlError = computed<string | null>(() => {
  const raw = webhookUrl.value.trim();
  if (raw.length === 0) return null;
  if (!raw.startsWith('https://'))
    return 'Please enter a URL starting with https://';
  let host: string;
  try {
    host = new URL(raw).hostname.toLowerCase();
  } catch {
    return 'Please enter a valid URL';
  }
  if (!host) return 'Please enter a valid URL';
  if (
    host.startsWith('[') ||
    host.includes(':') ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(host)
  )
    return 'Enter a hostname, not an IP address';
  if (
    host === 'localhost' ||
    !host.includes('.') ||
    /\.(local|internal|localhost|lan)$/.test(host)
  )
    return 'Enter a public hostname, not localhost or an internal address';
  return null;
});

const webhookUrlStatus = computed(() =>
  webhookUrlError.value ? 'danger' : 'default',
);

// Form validation
const isDisabled = computed(() => {
  return (
    selectedEventTypes.value.length === 0 ||
    webhookName.value.trim().length === 0 ||
    webhookUrl.value.trim().length === 0 ||
    webhookUrlStatus.value === 'danger'
  );
});

// Watch for webhook data changes to populate form in edit mode
watch(
  () => webhookData,
  (newData) => {
    if (newData && isEdit) {
      webhookName.value = newData.name || '';
      webhookUrl.value = newData.target_url || '';
      const allEventTypes = (newData.event_types as WebhookEventType[]) || [];
      selectedEventTypes.value = allEventTypes.filter((type) =>
        visibleEventTypeValues.value.includes(type),
      );
      hiddenEventTypes.value = allEventTypes.filter(
        (type) => !visibleEventTypeValues.value.includes(type),
      );
    }
  },
  { immediate: true },
);

function handleClose() {
  emit('closed');
}

async function handleCreate() {
  if (isCreating.value) return;
  isCreating.value = true;

  const { postIntegrationProfileSettings, refreshIntegrationProfileStore } =
    useIntegrationProfileStore();
  const { createWebhook, editWebhookConfig } = useWebhooksStore();

  const payload = {
    name: webhookName.value,
    target_url: webhookUrl.value,
    event_types: [
      ...selectedEventTypes.value,
      ...hiddenEventTypes.value,
    ] as WebhookEventType[],
  };

  if (isEdit && webhookData?.id) {
    await editWebhookConfig(webhookData.id, payload);
  } else {
    const { data: webhookIntegrationProfile, error } =
      await postIntegrationProfileSettings({
        population_settings: [],
        standard_population_settings: [],
        integration_type: 'webhook',
      });
    if (error) {
      isCreating.value = false;
      throw error;
    }

    // Refresh the integration profile store to include the new webhook profile
    await refreshIntegrationProfileStore();

    const createdWebhook = await createWebhook({
      ...payload,
      filters: [
        {
          filter_type: 'profile',
          profile_id: webhookIntegrationProfile.profile_id,
        },
      ],
    });
    if (createdWebhook) {
      emit('webhook-created', { webhookId: createdWebhook.id });
    }
  }

  handleClose();
  isCreating.value = false;
}
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";
.c-webhook-config-modal.o-bitts-modal.bitts-modal--data:deep() {
  .o-bitts-modal__header {
    @apply pr-60;
  }
  .o-bitts-modal__content {
    @apply p-0 m-0;
  }
}
</style>
